// Style MapLibre de la carte territoriale, construit à partir des jetons de la charte (section
// « Carte » de CHARTE.md, lus dans les variables CSS pour suivre le mode jour ou nuit) : fond
// vectoriel libre (schéma OpenMapTiles, servi par OpenFreeMap sans clé), puis les couches du
// territoire : voisins en retrait, masque grisé hors du territoire, contour accent, le tout
// déclaratif pour pouvoir être rebâti à chaque changement d'échelle ou de thème.
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';
import { collection, masque, type Territoire } from './geo';

export interface JetonsCarte {
  eau: string;
  terre: string;
  arr: string;
  arrLigne: string;
  sel: string;
  routes: string;
  voile: string;
  accent: string;
  texte2: string;
  texte3: string;
  ligne2: string;
  nuit: boolean;
}

/** Valeurs de la charte (mode jour) utilisées quand les variables CSS sont absentes. */
const JOUR: JetonsCarte = {
  eau: '#cfdee4',
  terre: '#e9e6de',
  arr: '#dcd8cc',
  arrLigne: '#9c978a',
  sel: '#ffffff',
  routes: '#ffffff',
  voile: 'rgba(245, 244, 240, 0.55)',
  accent: '#e3a42d',
  texte2: '#3a3f47',
  texte3: '#5c626b',
  ligne2: '#d3cfc4',
  nuit: false,
};

/** Fond de carte : adresse TileJSON des tuiles vectorielles et gabarit des glyphes. */
export interface FondCarte {
  tuiles: string;
  glyphes: string;
  libelle: string;
}

/** OpenFreeMap : tuiles vectorielles OpenMapTiles libres, sans clé ni limite d'usage, attribution obligatoire. */
export const FOND_OPENFREEMAP: FondCarte = {
  tuiles: 'https://tiles.openfreemap.org/planet',
  glyphes: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  libelle: 'OpenFreeMap, © OpenMapTiles, données © les contributeurs d\'OpenStreetMap',
};

export function estModeNuit(racine: HTMLElement = document.documentElement): boolean {
  const theme = racine.getAttribute('data-theme');
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Lit les jetons de la carte dans les variables CSS de la racine (thème courant). */
export function lireJetons(racine: HTMLElement = document.documentElement): JetonsCarte {
  const s = getComputedStyle(racine);
  const v = (nom: string, defaut: string) => s.getPropertyValue(nom).trim() || defaut;
  return {
    eau: v('--cc-carte-eau', JOUR.eau),
    terre: v('--cc-carte-terre', JOUR.terre),
    arr: v('--cc-carte-arr', JOUR.arr),
    arrLigne: v('--cc-carte-arr-ligne', JOUR.arrLigne),
    sel: v('--cc-carte-sel', JOUR.sel),
    routes: v('--cc-carte-routes', JOUR.routes),
    voile: v('--cc-carte-voile', JOUR.voile),
    accent: v('--cc-accent', JOUR.accent),
    texte2: v('--cc-encre-2', JOUR.texte2),
    texte3: v('--cc-encre-3', JOUR.texte3),
    ligne2: v('--cc-ligne-2', JOUR.ligne2),
    nuit: estModeNuit(racine),
  };
}

const ROUTES_PRINCIPALES = ['motorway', 'trunk', 'primary'];
const ROUTES_SECONDAIRES = ['secondary', 'tertiary'];
const ROUTES_LOCALES = ['minor', 'service', 'track', 'path', 'raceway', 'busway'];

function couchesFond(j: JetonsCarte): LayerSpecification[] {
  const largeur = (principale: number, secondaire: number, locale: number): unknown => [
    'interpolate',
    ['linear'],
    ['zoom'],
    8,
    ['case', ['in', ['get', 'class'], ['literal', ROUTES_PRINCIPALES]], principale * 0.4, ['in', ['get', 'class'], ['literal', ROUTES_SECONDAIRES]], secondaire * 0.3, 0],
    13,
    ['case', ['in', ['get', 'class'], ['literal', ROUTES_PRINCIPALES]], principale, ['in', ['get', 'class'], ['literal', ROUTES_SECONDAIRES]], secondaire, locale * 0.6],
    17,
    ['case', ['in', ['get', 'class'], ['literal', ROUTES_PRINCIPALES]], principale * 4, ['in', ['get', 'class'], ['literal', ROUTES_SECONDAIRES]], secondaire * 3.5, locale * 3],
  ];
  const police = ['Noto Sans Regular'];
  return [
    { id: 'fond-vegetation', type: 'fill', source: 'fond', 'source-layer': 'landcover', filter: ['in', ['get', 'class'], ['literal', ['wood', 'grass', 'farmland', 'wetland']]], paint: { 'fill-color': j.arr, 'fill-opacity': 0.45 } },
    { id: 'fond-residentiel', type: 'fill', source: 'fond', 'source-layer': 'landuse', filter: ['in', ['get', 'class'], ['literal', ['residential', 'suburb', 'neighbourhood', 'commercial', 'industrial', 'retail']]], paint: { 'fill-color': j.arr, 'fill-opacity': 0.55 } },
    { id: 'fond-parc', type: 'fill', source: 'fond', 'source-layer': 'park', paint: { 'fill-color': j.arr, 'fill-opacity': 0.5 } },
    { id: 'fond-aeroport', type: 'fill', source: 'fond', 'source-layer': 'aeroway', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': j.arr } },
    { id: 'fond-eau', type: 'fill', source: 'fond', 'source-layer': 'water', paint: { 'fill-color': j.eau } },
    { id: 'fond-cours-eau', type: 'line', source: 'fond', 'source-layer': 'waterway', paint: { 'line-color': j.eau, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 14, 2.5] } },
    { id: 'fond-batiments', type: 'fill', source: 'fond', 'source-layer': 'building', minzoom: 14, paint: { 'fill-color': j.arrLigne, 'fill-opacity': 0.22 } },
    { id: 'fond-routes-bord', type: 'line', source: 'fond', 'source-layer': 'transportation', filter: ['in', ['get', 'class'], ['literal', [...ROUTES_PRINCIPALES, ...ROUTES_SECONDAIRES, ...ROUTES_LOCALES]]], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': j.arrLigne, 'line-width': largeur(3.2, 2.6, 2) as number, 'line-opacity': 0.6 } },
    { id: 'fond-routes', type: 'line', source: 'fond', 'source-layer': 'transportation', filter: ['in', ['get', 'class'], ['literal', [...ROUTES_PRINCIPALES, ...ROUTES_SECONDAIRES, ...ROUTES_LOCALES]]], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': j.routes, 'line-width': largeur(2.4, 1.8, 1.2) as number } },
    { id: 'fond-rails', type: 'line', source: 'fond', 'source-layer': 'transportation', filter: ['==', ['get', 'class'], 'rail'], paint: { 'line-color': j.arrLigne, 'line-width': 1, 'line-dasharray': [4, 3] } },
    { id: 'fond-limites', type: 'line', source: 'fond', 'source-layer': 'boundary', filter: ['all', ['<=', ['get', 'admin_level'], 6], ['!=', ['get', 'maritime'], 1]], paint: { 'line-color': j.arrLigne, 'line-width': 1, 'line-dasharray': [5, 4] } },
    { id: 'fond-noms-eau', type: 'symbol', source: 'fond', 'source-layer': 'water_name', layout: { 'text-field': ['coalesce', ['get', 'name:fr'], ['get', 'name']], 'text-font': ['Noto Sans Italic'], 'text-size': 12 }, paint: { 'text-color': j.texte3, 'text-halo-color': j.eau, 'text-halo-width': 1 } },
    { id: 'fond-noms-routes', type: 'symbol', source: 'fond', 'source-layer': 'transportation_name', minzoom: 14, layout: { 'symbol-placement': 'line', 'text-field': ['coalesce', ['get', 'name:fr'], ['get', 'name']], 'text-font': police, 'text-size': 11 }, paint: { 'text-color': j.texte3, 'text-halo-color': j.terre, 'text-halo-width': 1 } },
    { id: 'fond-noms-quartiers', type: 'symbol', source: 'fond', 'source-layer': 'place', minzoom: 11, filter: ['in', ['get', 'class'], ['literal', ['suburb', 'quarter', 'neighbourhood', 'village', 'hamlet']]], layout: { 'text-field': ['coalesce', ['get', 'name:fr'], ['get', 'name']], 'text-font': police, 'text-size': 11, 'text-transform': 'none' }, paint: { 'text-color': j.texte3, 'text-halo-color': j.terre, 'text-halo-width': 1.2 } },
    { id: 'fond-noms-villes', type: 'symbol', source: 'fond', 'source-layer': 'place', filter: ['in', ['get', 'class'], ['literal', ['city', 'town']]], layout: { 'text-field': ['coalesce', ['get', 'name:fr'], ['get', 'name']], 'text-font': ['Noto Sans Bold'], 'text-size': ['interpolate', ['linear'], ['zoom'], 5, 11, 12, 15] }, paint: { 'text-color': j.texte2, 'text-halo-color': j.terre, 'text-halo-width': 1.4 } },
  ];
}

export interface DonneesTerritoire {
  echelle: Territoire;
  /** Surfaces à dessiner par-dessus le territoire (emprises d'autorisations du domaine public, lot L2) : polygones WGS84. */
  surfaces?: FeatureCollection<Polygon | MultiPolygon> | null;
}

/** Style complet : fond (s'il est fourni), voisins, masque, contour, surfaces. */
export function construireStyle(j: JetonsCarte, fond: FondCarte | null, donnees: DonneesTerritoire): StyleSpecification {
  const t = donnees.echelle;
  const surfaces: FeatureCollection<Polygon | MultiPolygon> = donnees.surfaces ?? { type: 'FeatureCollection', features: [] };
  const voisins = collection((t.voisins ?? []).map((v) => ({ geometrie: v.geometrie, proprietes: { libelle: v.libelle, indicatif: v.indicatif } })));
  const contour = collection(t.geometrie ? [{ geometrie: t.geometrie, proprietes: { libelle: t.libelle, indicatif: t.indicatif } }] : []);
  const tirets = (indicatif: boolean) => (indicatif ? [3, 3] : [5, 4]);
  const couchesTerritoire: LayerSpecification[] = [
    { id: 'voisins-fond', type: 'fill', source: 'voisins', paint: { 'fill-color': j.arr, 'fill-opacity': 0.35 } },
    { id: 'masque', type: 'fill', source: 'masque', paint: { 'fill-color': j.voile } },
    { id: 'voisins-ligne', type: 'line', source: 'voisins', paint: { 'line-color': j.arrLigne, 'line-width': 1.2, 'line-dasharray': ['case', ['get', 'indicatif'], ['literal', tirets(true)], ['literal', tirets(false)]] } },
    { id: 'contour-halo', type: 'line', source: 'contour', paint: { 'line-color': j.sel, 'line-width': 5, 'line-opacity': 0.6 } },
    { id: 'contour', type: 'line', source: 'contour', layout: { 'line-join': 'round' }, paint: { 'line-color': j.accent, 'line-width': 2, ...(t.indicatif ? { 'line-dasharray': [4, 3] } : {}) } },
    // Surfaces (emprises) : remplissage accent translucide et contour net, au-dessus du masque.
    { id: 'surfaces-fond', type: 'fill', source: 'surfaces', paint: { 'fill-color': j.accent, 'fill-opacity': 0.28 } },
    { id: 'surfaces-ligne', type: 'line', source: 'surfaces', layout: { 'line-join': 'round' }, paint: { 'line-color': j.accent, 'line-width': 1.5 } },
  ];
  return {
    version: 8,
    ...(fond ? { glyphs: fond.glyphes } : {}),
    sources: {
      ...(fond ? { fond: { type: 'vector', url: fond.tuiles } } : {}),
      voisins: { type: 'geojson', data: voisins },
      // Sans limite connue, rien n'est grisé : la carte dit qu'aucune limite n'est disponible.
      masque: { type: 'geojson', data: t.geometrie ? masque(t.geometrie) : { type: 'FeatureCollection', features: [] } },
      contour: { type: 'geojson', data: contour },
      surfaces: { type: 'geojson', data: surfaces },
    },
    layers: [{ id: 'terre', type: 'background', paint: { 'background-color': j.terre } }, ...(fond ? couchesFond(j) : []), ...couchesTerritoire],
  };
}
